import { Mail, Pencil, Phone, Trash2, Users, X } from 'lucide-react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { h3, h4, p, sh2, sh3, textStyle } from '../../styles/typography';
import Card from '../ui/card';
import IconTextButton from '../ui/IconTextButton';
import PrimaryButton from '../ui/PrimaryButton';
import './HyperLocalReferralTracker.css';
import { createReferralCode, deleteInfluencer } from './referralTrackerApi';

const ReferralDetails = ({ influencer, onClose, onDelete, onEdit, onCodeGenerated, showActionButtons = true }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [generateInProgress, setGenerateInProgress] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);

  if (!influencer) {
    console.log('[ReferralDetails] render: no influencer, skipping');
    return null;
  }

  console.log('[ReferralDetails] render', {
    influencer_id: influencer.influencer_id || influencer.id,
    name: influencer.name,
    referral_code: influencer.referral_code,
    referral_count: influencer.referral_count,
    showActionButtons,
  });

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const recentReferrals = influencer.recent_referrals || [];
  const influencerId = influencer.influencer_id || influencer.id;
  const hasReferralCode = !!(influencer.referral_code || generatedCode);

  const handleGenerateCode = async () => {
    if (!influencerId || hasReferralCode) return;
    setGenerateInProgress(true);
    setGenerateError('');
    try {
      const payload = { influencer_id: influencerId };
      if (influencer.site_id) payload.site_id = influencer.site_id;
      const res = await createReferralCode(payload);
      const code = res?.referral_code;
      if (code) setGeneratedCode(code);
      onCodeGenerated?.(res);
    } catch (err) {
      setGenerateError(err?.message || 'Failed to generate referral code.');
    } finally {
      setGenerateInProgress(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!influencerId) {
      console.log('[ReferralDetails] handleConfirmDelete skipped: no influencerId');
      return;
    }
    console.log('[ReferralDetails] handleConfirmDelete start', { influencerId, name: influencer.name });
    setDeleteInProgress(true);
    setDeleteError('');
    try {
      await deleteInfluencer(influencerId);
      console.log('[ReferralDetails] handleConfirmDelete success', { influencerId });
      setShowDeleteConfirm(false);
      onClose?.();
      onDelete?.(influencer);
    } catch (err) {
      console.error('[ReferralDetails] handleConfirmDelete error', err?.message || err);
      setDeleteError(err?.message || 'Failed to delete influencer.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  return (
    <div className="referral-tracker-details">
      <div className="referral-tracker-details-header">
        <h2 className={textStyle(h3, 'referral-tracker-details-title')}>
          Influencer Details
        </h2>
        <button
          type="button"
          onClick={() => {
            console.log('[ReferralDetails] close clicked');
            onClose?.();
          }}
          className="referral-tracker-details-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <Card variant="light" className="referral-tracker-details-card p-5">
        <div className="referral-tracker-details-name-row">
          <h3 className={textStyle(h4, 'referral-tracker-details-name')}>
            {influencer.name || 'Unnamed Influencer'}
          </h3>
          <span
            className={`referral-tracker-status referral-tracker-status--${influencer.status || 'active'}`}
          >
            {influencer.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div className="referral-tracker-details-grid">
          {/* <div className="referral-tracker-details-item">
            <MapPin className="referral-tracker-details-icon" />
            <div>
              <span className={textStyle(sh3, 'text-gray-600')}>Location / Region</span>
              <p className={p}>
                {[influencer.location, influencer.region].filter(Boolean).join(' · ') || '—'}
              </p>
            </div>
          </div> */}
          {(influencer.site_name ?? influencer.site_id) && (
            <div className="referral-tracker-details-item">
              <span className={textStyle(sh2, 'text-gray-600')}>Site</span>
              <p className={p}>{influencer.site_name ?? influencer.site_id}</p>
            </div>
          )}
          {(influencer.email != null && influencer.email !== '') && (
            <div className="referral-tracker-details-item">
              <Mail className="referral-tracker-details-icon" />
              <div>
                <span className={textStyle(sh3, 'text-gray-600')}>Email</span>
                <p className={p}>{influencer.email}</p>
              </div>
            </div>
          )}
          {(influencer.phone != null && influencer.phone !== '') && (
            <div className="referral-tracker-details-item">
              <Phone className="referral-tracker-details-icon" />
              <div>
                <span className={textStyle(sh3, 'text-gray-600')}>Phone</span>
                <p className={p}>{influencer.phone}</p>
              </div>
            </div>
          )}
        </div>

        <div className="referral-tracker-details-stat-row">
          <div className="referral-tracker-details-stat">
            <Users className="referral-tracker-stat-icon" />
            <span className={textStyle(sh2, 'referral-tracker-stat-value')}>
              {influencer.referral_count ?? 0}
            </span>
            <span className={textStyle(p, 'referral-tracker-stat-label')}>Total referrals</span>
          </div>
          <span className={textStyle(p, 'referral-tracker-stat-label')}>
            Referral code: {(influencer.referral_code || generatedCode) ? (
              <span className={textStyle(p, 'font-mono text-gray-600')}>{influencer.referral_code || generatedCode}</span>
            ) : (
              '—'
            )}
          </span>

          <div className="referral-tracker-details-item">
          <PrimaryButton
            type="button"
            onClick={handleGenerateCode}
            disabled={hasReferralCode || generateInProgress}
            className="w-auto h-auto px-6 py-3 text-base"
          >
            {generateInProgress ? 'Generating...' : 'Generate code'}
          </PrimaryButton>
          {generateError && (
            <p className={textStyle(p, 'text-red-600 mt-2')} role="alert">
              {generateError}
            </p>
          )}
        </div>
          <div className={textStyle(p, 'text-gray-500')}>
            Joined {formatDate(influencer.created_at)}
            {influencer.updated_at && ` · Updated ${formatDate(influencer.updated_at)}`}
            {' · '}Last referral {formatDate(influencer.last_referral_at)}
          </div>
        </div>
      </Card>

      {showActionButtons && (
        <div className="referral-tracker-details-actions flex justify-between items-center gap-4 mt-4">
          <IconTextButton
            icon={<Trash2 className="w-5 h-5" />}
            text="Delete Influencer"
            variant="danger"
            onClick={() => {
              console.log('[ReferralDetails] delete clicked, opening confirm');
              setDeleteError('');
              setShowDeleteConfirm(true);
            }}
          />
          <IconTextButton
            icon={<Pencil className="w-5 h-5" />}
            text="Edit Influencer"
            variant="success"
            onClick={() => {
              console.log('[ReferralDetails] edit clicked', { influencer_id: influencerId });
              onEdit?.(influencer);
            }}
          />
        </div>
      )}

      {recentReferrals.length > 0 && (
        <Card variant="light" className="referral-tracker-details-card p-5 mt-4">
          <h4 className={textStyle(sh2, 'mb-3')}>Recent referrals</h4>
          <ul className="referral-tracker-recent-list">
            {recentReferrals.map((ref, i) => (
              <li key={ref.customer_id || i} className="referral-tracker-recent-item">
                <span className={p}>{ref.customer_id}</span>
                <span className={textStyle(p, 'text-gray-500')}>
                  {formatDate(ref.referred_at)} · {ref.converted ? 'Converted' : 'Pending'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {showDeleteConfirm &&
        createPortal(
          <div
            className="referral-tracker-delete-confirm-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="referral-delete-confirm-title"
            onClick={() => {
              if (!deleteInProgress) {
                console.log('[ReferralDetails] delete confirm cancelled (backdrop)');
                setShowDeleteConfirm(false);
              }
            }}
          >
            <div
              className="referral-tracker-delete-confirm-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <Card variant="light" className="p-5">
                <h3 id="referral-delete-confirm-title" className={textStyle(h4, 'text-gray-900 mb-2')}>
                  Delete Influencer?
                </h3>
                <p className={textStyle(p, 'text-gray-600 mb-5')}>
                  Are you sure you want to delete <strong>{influencer.name || 'Unnamed Influencer'}</strong>?
                  This action cannot be undone. and the related data would able gets deleted.
                </p>
                {deleteError && (
                  <p className={textStyle(p, 'text-red-600 mb-3')} role="alert">
                    {deleteError}
                  </p>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      console.log('[ReferralDetails] delete confirm cancelled (Cancel button)');
                      setShowDeleteConfirm(false);
                    }}
                    disabled={deleteInProgress}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <IconTextButton
                    icon={<Trash2 className="w-5 h-5" />}
                    text={deleteInProgress ? 'Deleting...' : 'Delete'}
                    variant="danger"
                    disabled={deleteInProgress}
                    onClick={handleConfirmDelete}
                  />
                </div>
              </Card>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ReferralDetails;
